import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createTenantWorkflow,
  createTenantWorkflowVersion,
  deleteTenantWorkflowDraft,
  getApprovedWorkflowComponents,
  listTenantWorkflows,
  publishTenantWorkflow,
  TenantWorkflowDefinition,
  TenantWorkflowNode,
  TenantWorkflowTransition,
  TenantWorkflowVersion,
  updateTenantWorkflowDraft,
  validateTenantWorkflow,
  WorkflowApprovedComponents,
  WorkflowBusinessRules,
} from "@/api/tenantWorkflowApi";

export const WORKFLOW_DIRTY_KEY = "super_admin_workflow_unsaved";

export type WorkflowEditorState = {
  nodes: TenantWorkflowNode[];
  transitions: TenantWorkflowTransition[];
  businessRules: WorkflowBusinessRules;
};

const EMPTY_RULES: WorkflowBusinessRules = {
  funding: { funding_type: "single_year", number_of_years: 1 },
  renewal: { renewal_rule: "automatic" },
  mentoring: { required: false, completion_criteria: [] },
  interview: { required: false },
  approvals: { management_required: true, client_governance_required: false },
  eligibility: {},
  evidence: {},
  scoring: {},
  budget: {},
};

function editorFromVersion(version: TenantWorkflowVersion): WorkflowEditorState {
  return {
    nodes: [...version.nodes].sort((left, right) => left.display_order - right.display_order),
    transitions: [...version.transitions],
    businessRules: structuredClone(version.business_rules),
  };
}

function replaceVersion(
  definitions: TenantWorkflowDefinition[],
  version: TenantWorkflowVersion
): TenantWorkflowDefinition[] {
  return definitions.map((definition) =>
    definition.id === version.workflow_definition_id
      ? {
          ...definition,
          updated_at: version.updated_at ?? definition.updated_at,
          versions: definition.versions.map((candidate) =>
            candidate.id === version.id ? version : candidate
          ),
        }
      : definition
  );
}

function readError(error: unknown, fallback: string): string {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    const data = error.response.data as { detail?: unknown };
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail)) {
      return data.detail
        .map((item) =>
          item && typeof item === "object" && "msg" in item
            ? String(item.msg)
            : String(item)
        )
        .join(" ");
    }
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function readDeepLink() {
  if (typeof window === "undefined") return { definitionId: null, versionId: null };
  const params = new URLSearchParams(window.location.search);
  const definitionId = Number(params.get("workflow"));
  const versionId = Number(params.get("workflowVersion"));
  return {
    definitionId: Number.isFinite(definitionId) && definitionId > 0 ? definitionId : null,
    versionId: Number.isFinite(versionId) && versionId > 0 ? versionId : null,
  };
}

export function useTenantWorkflowWorkspace(tenantId: number | null) {
  const [definitions, setDefinitions] = useState<TenantWorkflowDefinition[]>([]);
  const [components, setComponents] = useState<WorkflowApprovedComponents | null>(null);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<number | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [editor, setEditorState] = useState<WorkflowEditorState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const selectedDefinition = useMemo(
    () => definitions.find((item) => item.id === selectedDefinitionId) ?? null,
    [definitions, selectedDefinitionId]
  );
  const selectedVersion = useMemo(
    () =>
      selectedDefinition?.versions.find((item) => item.id === selectedVersionId) ?? null,
    [selectedDefinition, selectedVersionId]
  );

  const updateDeepLink = useCallback((definitionId: number | null, versionId: number | null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("section", "workflows");
    if (definitionId) url.searchParams.set("workflow", String(definitionId));
    else url.searchParams.delete("workflow");
    if (versionId) url.searchParams.set("workflowVersion", String(versionId));
    else url.searchParams.delete("workflowVersion");
    window.history.replaceState({}, "", url);
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) {
      setDefinitions([]);
      setComponents(null);
      setSelectedDefinitionId(null);
      setSelectedVersionId(null);
      setEditorState(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [approved, rows] = await Promise.all([
        getApprovedWorkflowComponents(),
        listTenantWorkflows(),
      ]);
      setComponents(approved);
      setDefinitions(rows);
      const deepLink = readDeepLink();
      const definition =
        rows.find((item) => item.id === deepLink.definitionId) ??
        rows.find((item) => item.id === selectedDefinitionId) ??
        null;
      const version = definition
        ? definition.versions.find((item) => item.id === deepLink.versionId) ??
          definition.versions.find((item) => item.id === selectedVersionId) ??
          [...definition.versions].sort((left, right) => right.version - left.version)[0] ??
          null
        : null;
      setSelectedDefinitionId(definition?.id ?? null);
      setSelectedVersionId(version?.id ?? null);
      setEditorState(version ? editorFromVersion(version) : null);
      setDirty(false);
      updateDeepLink(definition?.id ?? null, version?.id ?? null);
    } catch (caught) {
      setError(readError(caught, "Workflows could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [selectedDefinitionId, selectedVersionId, tenantId, updateDeepLink]);

  useEffect(() => {
    void load();
    // Selection changes are handled explicitly; tenant changes own this reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    sessionStorage.setItem(WORKFLOW_DIRTY_KEY, String(dirty));
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirty]);

  function confirmDiscard(): boolean {
    return !dirty || window.confirm("Discard the unsaved workflow changes?");
  }

  function chooseVersion(definition: TenantWorkflowDefinition, version: TenantWorkflowVersion) {
    if (!confirmDiscard()) return;
    setSelectedDefinitionId(definition.id);
    setSelectedVersionId(version.id);
    setEditorState(editorFromVersion(version));
    setDirty(false);
    setError(null);
    setMessage(null);
    updateDeepLink(definition.id, version.id);
  }

  function chooseDefinition(definition: TenantWorkflowDefinition) {
    const version = [...definition.versions].sort((left, right) => {
      if (left.lifecycle_state === "draft" && right.lifecycle_state !== "draft") return -1;
      if (right.lifecycle_state === "draft" && left.lifecycle_state !== "draft") return 1;
      return right.version - left.version;
    })[0];
    if (version) chooseVersion(definition, version);
  }

  function closeEditor() {
    if (!confirmDiscard()) return;
    setSelectedDefinitionId(null);
    setSelectedVersionId(null);
    setEditorState(null);
    setDirty(false);
    updateDeepLink(null, null);
  }

  function setEditor(next: WorkflowEditorState) {
    setEditorState(next);
    setDirty(true);
    setMessage(null);
  }

  async function createDefinition(payload: {
    program_code: string;
    name: string;
    description?: string | null;
    seed_workflow_template_id?: number | null;
    source_scholarship_standard_id?: number | null;
  }) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const created = await createTenantWorkflow(payload);
      const version = created.versions[0];
      setDefinitions((current) => [...current, created]);
      setSelectedDefinitionId(created.id);
      setSelectedVersionId(version?.id ?? null);
      setEditorState(version ? editorFromVersion(version) : null);
      setDirty(false);
      updateDeepLink(created.id, version?.id ?? null);
      setMessage("Workflow draft created.");
      return true;
    } catch (caught) {
      setError(readError(caught, "The workflow draft could not be created."));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft(): Promise<TenantWorkflowVersion | null> {
    if (!selectedDefinition || !selectedVersion || !editor) return null;
    if (selectedVersion.lifecycle_state !== "draft") {
      setError("Published and validated versions cannot be edited.");
      return null;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await updateTenantWorkflowDraft(
        selectedDefinition.id,
        selectedVersion.id,
        {
          nodes: editor.nodes.map((node, index) => ({
            node_code: node.node_code,
            display_name: node.display_name,
            display_order: index,
          })),
          transitions: editor.transitions.map((transition) => ({
            from_status: transition.from_status,
            to_status: transition.to_status,
            action_code: transition.action_code,
          })),
          business_rules: editor.businessRules,
        }
      );
      setDefinitions((current) => replaceVersion(current, saved));
      setEditorState(editorFromVersion(saved));
      setDirty(false);
      setMessage("Draft saved.");
      return saved;
    } catch (caught) {
      setError(readError(caught, "The draft could not be saved."));
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function deleteDraft() {
    if (!selectedDefinition || !selectedVersion) return;
    if (selectedVersion.lifecycle_state !== "draft") {
      setError("Only draft workflow versions can be deleted.");
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await deleteTenantWorkflowDraft(selectedDefinition.id, selectedVersion.id);
      const remainingVersions = selectedDefinition.versions.filter(
        (version) => version.id !== selectedVersion.id
      );
      if (remainingVersions.length === 0) {
        setDefinitions((current) =>
          current.filter((definition) => definition.id !== selectedDefinition.id)
        );
        setSelectedDefinitionId(null);
        setSelectedVersionId(null);
        setEditorState(null);
        updateDeepLink(null, null);
        setMessage("Draft deleted.");
      } else {
        const nextVersion = [...remainingVersions].sort(
          (left, right) => right.version - left.version
        )[0];
        setDefinitions((current) =>
          current.map((definition) =>
            definition.id === selectedDefinition.id
              ? { ...definition, versions: remainingVersions }
              : definition
          )
        );
        setSelectedVersionId(nextVersion.id);
        setEditorState(editorFromVersion(nextVersion));
        updateDeepLink(selectedDefinition.id, nextVersion.id);
        setMessage("Draft deleted. Published versions are unchanged.");
      }
      setDirty(false);
    } catch (caught) {
      setError(readError(caught, "The draft could not be deleted."));
    } finally {
      setSaving(false);
    }
  }

  async function validateDraft() {
    if (!selectedDefinition || !selectedVersion) return;
    const version = dirty ? await saveDraft() : selectedVersion;
    if (!version) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const validated = await validateTenantWorkflow(selectedDefinition.id, version.id);
      setDefinitions((current) => replaceVersion(current, validated));
      setEditorState(editorFromVersion(validated));
      setDirty(false);
      setMessage("Validation passed. This version is ready to publish.");
    } catch (caught) {
      setError(readError(caught, "Validation did not pass."));
    } finally {
      setSaving(false);
    }
  }

  async function publishVersion() {
    if (!selectedDefinition || !selectedVersion) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await publishTenantWorkflow(selectedDefinition.id, selectedVersion.id);
      await load();
      setMessage("Workflow published. Published versions are read-only.");
    } catch (caught) {
      setError(readError(caught, "The workflow could not be published."));
    } finally {
      setSaving(false);
    }
  }

  async function startNewVersion(source: TenantWorkflowVersion) {
    if (!selectedDefinition || !confirmDiscard()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const created = await createTenantWorkflowVersion(selectedDefinition.id, source.id);
      setDefinitions((current) =>
        current.map((definition) =>
          definition.id === selectedDefinition.id
            ? { ...definition, versions: [...definition.versions, created] }
            : definition
        )
      );
      setSelectedVersionId(created.id);
      setEditorState(editorFromVersion(created));
      setDirty(false);
      updateDeepLink(selectedDefinition.id, created.id);
      setMessage("New draft version created. The published version is unchanged.");
    } catch (caught) {
      setError(readError(caught, "A new workflow version could not be created."));
    } finally {
      setSaving(false);
    }
  }

  return {
    closeEditor,
    components,
    createDefinition,
    deleteDraft,
    definitions,
    dirty,
    editor,
    error,
    loading,
    load,
    message,
    publishVersion,
    saveDraft,
    saving,
    selectedDefinition,
    selectedVersion,
    setEditor,
    chooseDefinition,
    chooseVersion,
    startNewVersion,
    validateDraft,
  };
}
